<template>
  <div class="options-container">
    <div v-if="showCategory" class="headline mb-3">{{ category }}</div>
    <template v-if="fields">
      <v-row>
        <v-col v-for="(components, group) in filteredComponents" :key="`${category}-${group}`" :cols="groupCols">
          <v-card class="pb-2" outlined :style="{ height: groupCols !== 12 ? '100%' : null }">
            <v-card-subtitle class="px-3 py-3 mb-2 grey lighten-4">
              {{ group }}
            </v-card-subtitle>
            <v-container>
              <v-row>
                <template v-for="component in components">
                  <v-col
                    v-if="!component.hidden"
                    :key="`${category}-${group}-${component.model}`"
                    :cols="getComponentCols(component)"
                  >
                    <component
                      :is="component.name"
                      v-bind="component.props"
                      :component="component"
                      :disabled="isComponentDisabled(component)"
                      :error="hasError(component)"
                      :error-messages="hasError(component) ? ['Property missing!'] : undefined"
                      :is-disabled="isComponentDisabled(component)"
                      :is-loading="isLoading"
                      :items="getItems(component)"
                      :offers="offers"
                      :required="component.required"
                      :entities="entities"
                      :loading="isLoading"
                      :value="componentValue(component)"
                      :datetime="componentValue(component)"
                      :enable-text-color="enableTextColor"
                      :site-groups="siteGroups"
                      :toolbar-buttons="froalaToolbarButtons"
                      :text-field-props="{ dense: true, class: 'mt-0', ...component.props }"
                      :pattern="component.pattern"
                      :custom-type="getCustomType(component)"
                      persistent-hint
                      hide-details="auto"
                      class="mt-0"
                      color="primary"
                      item-text="key"
                      rows="1"
                      format="DDMMYYYY"
                      auto-grow
                      dense
                      @input="onInput($event, component)"
                    />
                  </v-col>
                  <!-- Checkbox converts empty arrays to null to prevent overwriting default data. -->
                  <v-col
                    v-if="!isDefault && component.overwriteDefaultWithEmpty"
                    :key="`${category}-${group}-${component.model}-checkbox`"
                    col="4"
                    class="d-flex align-center"
                  >
                    <v-checkbox
                      :input-value="Array.isArray(componentValue(component))"
                      :disabled="
                        isComponentDisabled(component) ||
                          !!(Array.isArray(componentValue(component)) && componentValue(component).length)
                      "
                      class="mt-0"
                      label="Overwrite Default"
                      hide-details
                      @change="onInput(componentValue(component) == null ? [] : null, component)"
                    />
                  </v-col>
                </template>
              </v-row>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </template>
    <slot v-else />
  </div>
</template>

<script>
// Helpers
import { mapGetters } from 'vuex'
// Components
import DatePicker from '@/components/forms/date-picker'
import ColorPicker from '@/components/forms/color-picker'
import DatetimePicker from '@/components/datetime-picker'
import InputContainer from '@/components/forms/input-container'
import SelectImage from '@/components/forms/select-image'
import SelectList from '@/components/forms/select-list'
import TrueFalseList from '@/components/forms/true-false-list'
import TextareaInput from '@/components/forms/textarea-input'
import TextInput from '@/components/forms/text-input'
import ItemList from '@/components/forms/item-list'
import FroalaInput from '@/components/forms/froala-input'
import InputContainerList from '@/components/forms/input-container-list'
import SubcontextContainer from '@/components/forms/subcontext-container'
import ToggleInput from '@/components/forms/toggle-input'

export default {
  name: 'CategoryOptions',
  components: {
    DatePicker,
    ColorPicker,
    DatetimePicker,
    InputContainer,
    SelectImage,
    SelectList,
    TextareaInput,
    TextInput,
    TrueFalseList,
    ItemList,
    FroalaInput,
    InputContainerList,
    SubcontextContainer,
    ToggleInput,
  },
  props: {
    category: {
      type: String,
      required: true,
    },
    context: {
      type: String,
      default: undefined,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    enableImages: {
      type: Boolean,
      default: false,
    },
    enableShortcodes: {
      type: Boolean,
      default: false,
    },
    enableTextColor: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      default: true,
    },
    groupCols: {
      type: Number,
      default: 12,
    },
    componentCols: {
      type: Number,
      default: 12,
    },
    offers: {
      type: Array,
      default: () => [],
    },
    entities: {
      type: Array,
      default: () => [],
    },
    sites: {
      type: Array,
      default: () => [],
    },
    siteGroups: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    languages: {
      type: Array,
      default: () => [],
    },
    exclude: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Object,
      default: undefined,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    showCategory: {
      type: Boolean,
      default: true,
    },
    wpCategories: {
      type: Array,
      default: () => [],
    },
    wpTags: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Referring to default geo when data is split up by country.
     */
    isDefault: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['getItemsForOption']),
    filteredComponents() {
      if (!this.exclude.length) return this.fields
      return Object.entries(this.fields).reduce((obj, [field, components]) => {
        const filteredComponents = components.filter(({ model }) => !this.exclude.includes(model))
        if (filteredComponents.length) obj[field] = filteredComponents
        return obj
      }, {})
    },
    froalaToolbarButtons() {
      return () =>
        [
          'paragraphFormat',
          'align',
          this.enableTextColor && 'textColor',
          'bold',
          'underline',
          'italic',
          'strikeThrough',
          'clearFormatting',
          'insertTable',
          'insertVideo',
          'formatOL',
          'formatUL',
          this.enableImages && 'mediaGallery',
          'customLinkInsert',
          this.enableShortcodes && 'shortcodeInsert',
          'html',
        ].filter(Boolean)
    },
  },
  methods: {
    componentValue({ model }) {
      const property = this.data[model]

      if (!this.context) return property
      if (property == undefined) return undefined
      return property[this.context]
    },
    getComponentCols({ overwriteDefaultWithEmpty }) {
      return !this.isDefault && overwriteDefaultWithEmpty ? 8 : this.componentCols
    },
    getItems({ option }) {
      if (this.options[option]) return this.options[option]
      if (option === 'entities') return this.entities
      if (option === 'languages') return this.languages
      if (option === 'sites') return this.sites
      if (option === 'users') return this.users
      if (option === 'offers') return this.offers
      if (option === 'wp-categories') return this.wpCategories
      if (option === 'wp-tags') return this.wpTags
      return this.getItemsForOption(option)
    },
    getCustomType({ props, option }) {
      if (props && props.customType) return props.customType

      switch (option) {
        case 'entities.cryptoCoin':
          return 'symbol'
        case 'countries':
          return 'country'
        default:
          return null
      }
    },
    isComponentDisabled(component) {
      return !!(
        this.isLoading ||
        this.hasPageErrors ||
        this.isDisabled ||
        component.disabled ||
        (this.editMode && component.disableEdit)
      )
    },
    onInput(value, { model }) {
      this.$emit('input', { context: this.context, model, value })
    },
    hasError({ model, props: { type, required } }) {
      if (!required) return false
      const value = this.componentValue({ model })

      switch (type) {
        case 'string':
        case 'array':
          return value == undefined || !value.length
        case 'number':
          return value == undefined
        default:
          return value === undefined
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.options-container {
  height: 100%;
}
</style>
